import React, { createContext, ReactNode, useContext, useRef } from 'react';
import { createStore, StoreApi, useStore } from 'zustand';

import { useToggle } from '@/src/hooks/useToggle';

import { useInbox } from '../../../../store/inbox.store';
import { FileEntry } from '../../../../types';

export const COMMENTS_TAB_INDEX = 0;
export const TRANSACTION_TAB_INDEX = 1;

export type FileEntryViewerZustandStore = {
  fileEntry: FileEntry;
  activeTab: number;
  setActiveTab: (activeTab: number) => void;
  setFileEntry: (fileEntry: FileEntry) => void;
  isOpenTreatment: boolean;
  setIsOpenTreatment: (isOpen: boolean) => void;
};

type FileEntryViewerStore = FileEntryViewerZustandStore & {
  isFullScreen: boolean;
  toggleIsFullScreen: (isFullScreen?: boolean) => void;
};

const FileEntryViewerContext = createContext<FileEntryViewerStore>(undefined);

const createFileEntryViewerStore = ({ fileEntry, activeTab }) => {
  const DEFAULT_PROPS = {
    activeTab: 0,
    isOpenTreatment: false,
  };

  return createStore<FileEntryViewerZustandStore>()((set) => ({
    ...DEFAULT_PROPS,
    fileEntry: fileEntry,
    activeTab,
    setActiveTab: (activeTab: number) => set(() => ({ activeTab: activeTab })),
    setFileEntry: (fileEntry: FileEntry) => set(() => ({ fileEntry })),
    setIsOpenTreatment: (isOpen: boolean) => set(() => ({ isOpenTreatment: isOpen })),
  }));
};

type FileEntryViewerProviderProps = {
  fileEntry?: FileEntry;
  children: ReactNode;
};

export const FileEntryViewerProvider = ({ fileEntry, children }: FileEntryViewerProviderProps) => {
  const { isTransactionsEnabled } = useInbox();
  const storeRef = useRef<StoreApi<FileEntryViewerZustandStore>>();
  if (!storeRef.current) {
    storeRef.current = createFileEntryViewerStore({
      fileEntry,
      activeTab: isTransactionsEnabled ? TRANSACTION_TAB_INDEX : COMMENTS_TAB_INDEX,
    });
  }
  const [isFullScreen, toggleIsFullScreen] = useToggle(false);

  return (
    <FileEntryViewerContext.Provider
      value={{
        ...useStore(storeRef.current),
        isFullScreen,
        toggleIsFullScreen,
      }}
    >
      {children}
    </FileEntryViewerContext.Provider>
  );
};

export function useFileEntryViewer() {
  const store = useContext(FileEntryViewerContext);
  if (!store) {
    throw new Error('useFileEntryViewer must be used within a FileEntryViewerProvider');
  }
  return {
    ...store,
  };
}
